/**
 * Comms' part of the attention inbox (lib/inbox): notifications sent to me that ask for my
 * acknowledgement (R-28). They count in the top bar's bell; acknowledging one here (or in the
 * conversation) tells a flow waiting to escalate that it was seen.
 */
import { CheckCheck } from 'lucide-react';
import { msg, t } from '@/lib/i18n';
import type { InboxItem, InboxSource } from '@/lib/inbox';
import { Button } from '@/ui';
import { type PendingNotificationOut, pendingAcksQuery, useAcknowledge } from './api';
import { NOTIFY_CATEGORY, lbl } from './labels';

function AckButton({ id }: { id: string }) {
  const ack = useAcknowledge();
  return (
    <Button size="sm" variant="primary" icon={CheckCheck} loading={ack.isPending} onClick={() => ack.mutate(id)}>
      {t('Acknowledge')}
    </Button>
  );
}

export const acknowledgementsInbox: InboxSource<PendingNotificationOut[]> = {
  key: 'acknowledgements',
  label: msg('Notifications to acknowledge'),
  order: 5,
  permission: 'comms.notification.acknowledge',
  module: 'comms',
  bell: true,
  href: '/me/messages',
  query: pendingAcksQuery,
  items: (rows) =>
    rows.map((x): InboxItem => ({
      id: `ack:${x.message_id}`,
      source: 'acknowledgements',
      kind: msg('Notification'),
      tone: x.severity === 'high' ? 'bad' : 'caution',
      title: x.subject || x.text.split('\n')[0] || t('Notification'),
      detail: x.text,
      meta: x.category ? [lbl(NOTIFY_CATEGORY, x.category)] : undefined,
      at: x.created_at,
      href: `/me/messages/${x.conversation_id}`,
      rank: x.severity === 'high' ? 0 : 2,
      actions: <AckButton id={x.message_id} />,
    })),
};
